"use client";

import { useMemo, useState } from "react";
import {
  ErrorNotice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import { DerivativeResults } from "@/components/tools/calculus/derivative-results";
import {
  EXPRESSION_ERROR_STRINGS,
  expressionErrorMessage,
} from "@/components/tools/calculus/expression-errors";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  buildDerivativeReport,
  type ReportResult,
} from "@/lib/tool-kit/calculus/derivative-report";

/** English strings. A translated content file overrides these through `ui`. */
export const DERIVATIVE_CALCULATOR_STRINGS = {
  functionLabel: "Function to differentiate",
  functionHint:
    "Use + − * / ^ and brackets. 2x, x sin x and (x+1)(x−1) multiply. Functions: sin, cos, tan, sec, csc, cot, asin, acos, atan, sinh, cosh, tanh, exp, ln, log (base 10), sqrt, abs. Constants: pi and e. Decimals use a point.",
  variableLabel: "With respect to",
  orderLabel: "Order of the derivative",
  pointLabel: "Evaluate at {variable} = (optional)",
  pointHint: "A number or a constant such as 2, −1.5 or pi/4.",
  graphFromLabel: "Graph from {variable} =",
  graphToLabel: "to {variable} =",
  results: "Results",
  readAs: "Read as",
  derivativeHeading: "Derivative",
  copyAnswer: "Copy answer",
  copyLatex: "Copy LaTeX",
  copied: "Copied!",
  higherHeading: "All derivatives up to order {order}",
  valuesHeading: "At {variable} = {point}",
  undefinedHere: "undefined",
  pointInvalid:
    "Enter the point as a number or a constant expression such as 2, −1.5 or pi/4 (no letters other than pi and e).",
  noVariable:
    "This expression has no variable, so it is a constant and its derivative is 0.",
  tooComplex:
    "That expression is too large to differentiate here. Try a smaller piece of it.",
  truncated:
    "Higher derivatives stop at order {order}: the next one is too large to show.",
  absNote:
    "The derivative of |u| is undefined where u = 0 (the graph has a corner there).",
  otherLetters:
    "Letters other than {variable} are treated as constants. Choose another variable above to differentiate with respect to it.",
  stepsHeading: "Step-by-step working",
  stepsIntro:
    "Each line differentiates one part of the expression, outermost first, and names the rule used.",
  stepLine: "d/d{variable}[{expression}] = {result}",
  ruleTitles: {
    constant: "Constant rule",
    identity: "Derivative of the variable",
    "constant-multiple": "Constant multiple rule",
    sum: "Sum rule",
    power: "Power rule",
    "power-chain": "Power rule with the chain rule",
    exponential: "Exponential rule",
    "general-power": "General power (logarithmic differentiation)",
    product: "Product rule",
    quotient: "Quotient rule",
    chain: "Chain rule",
    elementary: "Standard derivative",
  },
  ruleText: {
    constant: "A constant does not change, so its derivative is 0.",
    identity: "The derivative of {v} with respect to {v} is 1.",
    "constant-multiple":
      "A constant factor comes out front: (c·g)′ = c·g′, with c = {c} and g = {u}.",
    sum: "The derivative of a sum is the sum of the derivatives of its terms.",
    power:
      "(xⁿ)′ = n·xⁿ⁻¹, here with n = {n}: bring the exponent down and lower it by 1.",
    "power-chain":
      "(uⁿ)′ = n·uⁿ⁻¹·u′, with u = {u} and n = {n}: the power rule, then multiply by the derivative of the inside.",
    exponential: "(aᵘ)′ = aᵘ·ln(a)·u′, with a = {a} and u = {u}.",
    exponentialE: "(eᵘ)′ = eᵘ·u′, with u = {u}.",
    "general-power":
      "The base and the exponent both contain the variable, so use (uᵛ)′ = uᵛ·(v′·ln u + v·u′/u), with u = {u} and v = {w}.",
    product:
      "(uv)′ = u′v + uv′, with u = {u} and v = {w}. Here u′ = {uPrime} and v′ = {wPrime}.",
    productMany:
      "With {count} factors that contain the variable, differentiate each one in turn and keep the others: (f₁f₂…fₙ)′ = f₁′f₂…fₙ + f₁f₂′…fₙ + … + f₁f₂…fₙ′.",
    quotient:
      "(u/v)′ = (u′v − uv′)/v², with u = {u} and v = {w}. Here u′ = {uPrime} and v′ = {wPrime}.",
    chain:
      "(f(u))′ = f′(u)·u′. The outer function is {fn}, the inside is u = {u}, so f′(u) = {outer}, and u′ = {uPrime}.",
    elementary: "The standard derivative of {fn}({v}) is {outer}.",
  },
  graphHeading: "Graph",
  graphTitle: "Graph of {original} and of its derivative {derived}",
  graphOther:
    "A graph needs a function of one variable, so it is hidden while other letters are present.",
  seriesFunction: "{name} (the function)",
  seriesDerivative: "{name} (the derivative)",
  pointLabelText: "({x}, {y})",
  downloadPng: "Download graph (PNG)",
  downloadSvg: "Download graph (SVG)",
  expressionErrors: EXPRESSION_ERROR_STRINGS,
} as const;

type Order = "1" | "2" | "3" | "4" | "5";

const ORDERS: readonly Order[] = ["1", "2", "3", "4", "5"];

function errorMessage(
  result: Extract<ReportResult, { ok: false }>,
  strings: typeof DERIVATIVE_CALCULATOR_STRINGS
): string {
  return "error" in result
    ? expressionErrorMessage(result.error, strings.expressionErrors)
    : strings.tooComplex;
}

export default function DerivativeCalculatorWidget() {
  const strings = useToolUi(DERIVATIVE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("x^2 sin(x)");
  const [chosen, setChosen] = useState("x");
  const [orderText, setOrderText] = useState<Order>("1");
  const [pointText, setPointText] = useState("1");
  const [fromText, setFromText] = useState("-5");
  const [toText, setToText] = useState("5");
  const order = Number(orderText);
  const result = useMemo(
    () => buildDerivativeReport(text, chosen, order),
    [text, chosen, order]
  );
  const report = result.ok ? result.report : null;
  const variable = report?.variable ?? chosen;
  const variableOptions = (report?.variables ?? []).map((name) => ({
    value: name,
    label: name,
  }));

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <TextField
            hint={strings.functionHint}
            id="dc-function"
            label={strings.functionLabel}
            mono
            onChange={setText}
            value={text}
          />
        </div>
        {variableOptions.length > 1 ? (
          <SelectField
            id="dc-variable"
            label={strings.variableLabel}
            onChange={setChosen}
            options={variableOptions}
            value={variable}
          />
        ) : null}
        <SelectField
          id="dc-order"
          label={strings.orderLabel}
          onChange={setOrderText}
          options={ORDERS.map((value) => ({ value, label: value }))}
          value={orderText}
        />
        <TextField
          hint={strings.pointHint}
          id="dc-point"
          label={t(strings.pointLabel, { variable })}
          mono
          onChange={setPointText}
          value={pointText}
        />
        <div className="grid grid-cols-2 gap-4">
          <TextField
            id="dc-from"
            label={t(strings.graphFromLabel, { variable })}
            mono
            numeric
            onChange={setFromText}
            value={fromText}
          />
          <TextField
            id="dc-to"
            label={t(strings.graphToLabel, { variable })}
            mono
            numeric
            onChange={setToText}
            value={toText}
          />
        </div>
      </div>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {result.ok ? null : (
          <ErrorNotice>{errorMessage(result, strings)}</ErrorNotice>
        )}
        {report ? (
          <DerivativeResults
            fmt={fmt}
            fromText={fromText}
            order={order}
            pointText={pointText}
            report={report}
            strings={strings}
            toText={toText}
          />
        ) : null}
      </section>
    </>
  );
}
